import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgModule, ModuleWithProviders } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgxEditorModule } from 'ngx-editor';
import { AirportTypeaheadComponent } from './airport-typeahead/airport-typeahead.component';
import { AppMessagesComponent } from './app-messages/app-messages.component';
import { CategoriesComponent } from './category-selector/categories.component';
import { CategoriesFilter } from './category-selector/categories.filter';
import { ContentBlockerComponent } from './content-blocker/content-blocker.component';
import { ContextNavComponent } from './context-nav/context-nav.component';
import { CountryTypeaheadComponent } from './country-typeahead.component';
import { DeleteModalComponent } from './delete-modal/delete-modal.component';
import { FlightSearchComponent } from './flight-search/flight-search.component';
import { FocusDirective } from './focus.directive';
import { FooterComponent } from './footer.component';
import { GenericTypeaheadComponent } from './generic-typeahead/generic-typeahead.component';
import { RhContextUtilityNavComponent } from './header/context-utility-nav.component';
import { HeaderLogoSwitcherComponent } from './header/header-logo-switcher.component';
import { RhHeaderNavComponent } from './header/header-nav.component';
import { RhHelpButtonComponent } from './help-button/help-button.component';
import { RhTabbedHelpSlideoutComponent } from './tabbed-help-slideout/tabbed-help-slideout.component';
import { HubMultiCheckDropdownComponent } from './hub-multi-check-dropdown.component';
import { HubTagInputStructuredComponent } from './hub-tag-input-structured.component';
import { LanguagePickerComponent } from './language/language-picker.component';
import { LanguagePickerV2Component } from './language-v2/language-picker-v2.component';
import { ListFormComponent } from './list-form/list-form.component';
import { TargetingByMarketComponent } from './markets/targeting-by-market.component';
import { NoContentComponent } from './no-content.component';
import { RhPageLoaderComponent } from './page-loading.component';
import { PreviewModalComponent } from './preview-modal/preview-modal.component';
import { ProfileComponent } from './profile/profile.component';
import { RhLoaderComponent } from './rh-loader.component';
import { RhLogoComponent } from './rh-logo.component';
import { ScrollAnchorDirective } from './scroll/scroll-anchor.directive';
import { ScrollContainerDirective } from './scroll/scroll-container.directive';
import { ScrollRefDirective } from './scroll/scroll-ref.directive';
import { RhSectionTabsComponent } from './section-tabs/section-tabs.component';
import { SelectedIdsOrTermsCategoryFilter } from './selected-ids-terms-category.filter';
import { SelectedIdsOrTermsUasFilter } from './selected-ids-terms-uas.filter';
import { SubfleetTypeaheadComponent } from './subfleet-typeahead.component';
import { TerminalTypeaheadComponent } from './terminal-typeahead.component';
import { UasFilter } from './uas-filter.filter';
import { SimpleFilter } from './simple-filter.filter';
import { MultiCheckDropdownComponent } from './multicheck-dropdown.component';
import { PseudoMultiSelectComponent } from './pseudo-multi-select/pseudo-multi-select.component';
import { RhButtonClearComponent } from './buttons/rh-button-clear.component';
import { RhButtonDeleteComponent } from './buttons/rh-button-delete.component';
import { PartnershipsFilter } from './filters/partnerships-filter.filter';
import { PartnersFilter } from './filters/partners-filter.filter';
import { RhCabinBrandFilterComponent } from './ua-filters/cabin-brand/rh-cabin-brand-filter.component';
import { RhCategoryFilterComponent } from './ua-filters/category/rh-category-filter.component';
import { RhChannelFilterComponent } from './ua-filters/channel/rh-channel-filter.component';
import { RhFilterTypeaheadTypeaheadComponent } from './ua-filters/rh-filter-typeahead.component';
import { RhSubfleetFilterComponent } from './ua-filters/subfleet/rh-subfleet-filter.component';
import { HubUiModule } from '@atpco/hub-ui';
import { GetDisplayCodePipe } from './language/get-display-code.pipe';
import { ModalDisplayValuePipe } from './context-nav/modal-display-value.pipe';
import { IsFilteringPipe } from './filters/is-filtering.pipe';
import { SharedPipesModule } from '../shared/shared-pipes/shared-pipes.module';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { RhUpaSourceComponent } from './ua-filters/source/rh-upa-source.component';
import { GetLinkToMarketPipe } from './markets/get-link-to-market.pipe';
import { CannotSubmitListFormPipe } from './list-form/cannot-submit-list-form.pipe';
import { GetCaptionPipe } from './preview-modal/get-caption.pipe';
import { ListFilterPlaceholderPipe } from './list-filter-placeholder.pipe';
import { CurrencyNamePipe } from './currency-name.pipe';
import { ObserversSidebarComponent } from '../observers/shared/observers-sidebar/observers-sidebar.component';
import { ExpandableAutocompleteComponent } from './expandable-autocomplete/expandable-autocomplete.component';
import { ExpandableListComponent } from './expandable-list/expandable-list.component';
import { FieldTooltipComponent } from './field-tooltip/field-tooltip.component';
import { FormattedTextEditorComponent } from './text-editor/formatted-text-editor.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { RhHelpSlideoverComponent } from './help-slideover/help-slideover.component';
import { SearchBarSetFilter } from './filters/search-bar-set-filter/search-bar-set-filter.component';
import { UpaFilter } from './filters/upa-filter.pipe';
import { StandardSearchBarComponent } from './standard-search-bar/standard-search-bar.component';
import { SearchBarSecondaryActionsComponent } from './standard-search-bar/search-bar-secondary-actions/search-bar-secondary-actions.component';
import { IconsComponent } from './icons/icons.component';
import { PrevNextButtonsComponent } from './prev-next-buttons/prev-next-buttons.component';
import { CarrierTypeaheadComponent } from './carrier-typeahead/carrier-typeahead.component';
import { TranslationsLanguagePickerComponent } from './translations-language-picker/translations-language-picker.component';
import { OnBoardingWizardComponent } from './on-boarding-wizard/on-boarding-wizard.component';
import { HubCheckDropdownComponent } from './hub-check-dropdown.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    RouterModule,
    HubUiModule,
    SharedPipesModule,
    NgxEditorModule,
    DragDropModule,
    ScrollingModule
  ],
  exports: [
    AppMessagesComponent,
    DeleteModalComponent,
    ContextNavComponent,
    SimpleFilter,
    SelectedIdsOrTermsCategoryFilter,
    SelectedIdsOrTermsUasFilter,
    RhLoaderComponent,
    LanguagePickerComponent,
    LanguagePickerV2Component,
    TranslationsLanguagePickerComponent,
    HubMultiCheckDropdownComponent,
    HubTagInputStructuredComponent,
    HeaderLogoSwitcherComponent,
    FooterComponent,
    FlightSearchComponent,
    AirportTypeaheadComponent,
    NoContentComponent,
    FocusDirective,
    UasFilter,
    MultiCheckDropdownComponent,
    HubCheckDropdownComponent,
    PseudoMultiSelectComponent,
    ScrollContainerDirective,
    GenericTypeaheadComponent,
    SubfleetTypeaheadComponent,
    TerminalTypeaheadComponent,
    CountryTypeaheadComponent,
    RhPageLoaderComponent,
    PreviewModalComponent,
    ScrollContainerDirective,
    ScrollRefDirective,
    ScrollAnchorDirective,
    CategoriesComponent,
    CategoriesFilter,
    RhLogoComponent,
    ListFormComponent,
    TargetingByMarketComponent,
    PartnershipsFilter,
    PartnersFilter,
    UpaFilter,
    RhHeaderNavComponent,
    RhHelpButtonComponent,
    RhHelpSlideoverComponent,
    RhTabbedHelpSlideoutComponent,
    RhContextUtilityNavComponent,
    RhSectionTabsComponent,
    RhCategoryFilterComponent,
    RhFilterTypeaheadTypeaheadComponent,
    RhChannelFilterComponent,
    RhCabinBrandFilterComponent,
    RhSubfleetFilterComponent,
    RhButtonClearComponent,
    RhButtonDeleteComponent,
    ContentBlockerComponent,
    ProfileComponent,
    IsFilteringPipe,
    BreadcrumbsComponent,
    RhUpaSourceComponent,
    ListFilterPlaceholderPipe,
    GetCaptionPipe,
    CurrencyNamePipe,
    ObserversSidebarComponent,
    ExpandableAutocompleteComponent,
    FieldTooltipComponent,
    FormattedTextEditorComponent,
    SearchBarSetFilter,
    StandardSearchBarComponent,
    SearchBarSecondaryActionsComponent,
    IconsComponent,
    PrevNextButtonsComponent,
    CarrierTypeaheadComponent,
    TranslationsLanguagePickerComponent,
    OnBoardingWizardComponent,
  ],
  declarations: [
    AppMessagesComponent,
    DeleteModalComponent,
    ContextNavComponent,
    SimpleFilter,
    SelectedIdsOrTermsCategoryFilter,
    SelectedIdsOrTermsUasFilter,
    RhLoaderComponent,
    LanguagePickerComponent,
    LanguagePickerV2Component,
    TranslationsLanguagePickerComponent,
    HubMultiCheckDropdownComponent,
    HubTagInputStructuredComponent,
    HeaderLogoSwitcherComponent,
    FooterComponent,
    FlightSearchComponent,
    AirportTypeaheadComponent,
    NoContentComponent,
    FocusDirective,
    UasFilter,
    MultiCheckDropdownComponent,
    HubCheckDropdownComponent,
    PseudoMultiSelectComponent,
    GenericTypeaheadComponent,
    SubfleetTypeaheadComponent,
    TerminalTypeaheadComponent,
    CountryTypeaheadComponent,
    RhPageLoaderComponent,
    PreviewModalComponent,
    ScrollContainerDirective,
    ScrollRefDirective,
    ScrollAnchorDirective,
    CategoriesComponent,
    CategoriesFilter,
    RhLogoComponent,
    ListFormComponent,
    TargetingByMarketComponent,
    PartnershipsFilter,
    PartnersFilter,
    UpaFilter,
    RhHeaderNavComponent,
    RhHelpButtonComponent,
    RhHelpSlideoverComponent,
    RhTabbedHelpSlideoutComponent,
    RhContextUtilityNavComponent,
    RhSectionTabsComponent,
    RhCategoryFilterComponent,
    RhFilterTypeaheadTypeaheadComponent,
    RhChannelFilterComponent,
    RhCabinBrandFilterComponent,
    RhSubfleetFilterComponent,
    RhButtonClearComponent,
    RhButtonDeleteComponent,
    ContentBlockerComponent,
    ProfileComponent,
    GetDisplayCodePipe,
    ModalDisplayValuePipe,
    IsFilteringPipe,
    BreadcrumbsComponent,
    RhUpaSourceComponent,
    GetLinkToMarketPipe,
    ListFilterPlaceholderPipe,
    CannotSubmitListFormPipe,
    GetCaptionPipe,
    CurrencyNamePipe,
    ObserversSidebarComponent,
    ExpandableAutocompleteComponent,
    ExpandableListComponent,
    FieldTooltipComponent,
    FormattedTextEditorComponent,
    SearchBarSetFilter,
    StandardSearchBarComponent,
    SearchBarSecondaryActionsComponent,
    IconsComponent,
    PrevNextButtonsComponent,
    CarrierTypeaheadComponent,
    TranslationsLanguagePickerComponent,
    OnBoardingWizardComponent,
  ],
})
export class RhCommonModule {
  static forRoot(): ModuleWithProviders<RhCommonModule> {
    return {
      ngModule: RhCommonModule,
    };
  }
}
